import { supabase, browserEnvironment } from '../../lib/supabase';
import { commandEndpoint } from '../../lib/command-endpoint';

export async function listPaymentOperations(organisationId: string) {
  const [configuration, attempts, payments, refunds, reviews, alerts] = await Promise.all([
    supabase
      .from('organisation_payment_configurations')
      .select('*')
      .eq('organisation_id', organisationId)
      .maybeSingle(),
    supabase
      .from('payment_attempts')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false })
      .limit(50),
    supabase
      .from('payment_admin_summaries')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('paid_at', { ascending: false })
      .limit(50),
    supabase
      .from('refunds')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false })
      .limit(50),
    supabase
      .from('payment_review_cases')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false })
      .limit(50),
    supabase
      .from('operational_alerts')
      .select('*')
      .eq('organisation_id', organisationId)
      .order('created_at', { ascending: false })
      .limit(50)
  ]);
  for (const result of [configuration, attempts, payments, refunds, reviews, alerts])
    if (result.error) throw result.error;
  return {
    configuration: configuration.data as PaymentConfiguration | null,
    attempts: attempts.data as PaymentAttempt[],
    payments: payments.data as Payment[],
    refunds: refunds.data as Refund[],
    reviews: reviews.data as ReviewCase[],
    alerts: alerts.data as Alert[]
  };
}

async function edgeCommand<T>(functionName: string, body: Record<string, unknown>) {
  const session = await supabase.auth.getSession();
  const token = session.data.session?.access_token;
  if (!token) throw new Error('Authentication required');
  const response = await fetch(
    commandEndpoint(browserEnvironment.VITE_SUPABASE_URL, functionName),
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: browserEnvironment.VITE_SUPABASE_PUBLISHABLE_KEY,
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify(body)
    }
  );
  const result = (await response.json()) as T & { error?: string };
  if (!response.ok) throw new Error(result.error ?? 'Payment operation failed');
  return result;
}

export const createConnectOnboardingLink = (organisationId: string) =>
  edgeCommand<{ url: string }>('stripe-connect', { action: 'onboarding-link', organisationId });
export const refreshConnectStatus = (organisationId: string) =>
  edgeCommand('stripe-connect', { action: 'refresh-status', organisationId });
export const resolvePaymentReview = (caseId: string, status: string, notes: string) =>
  edgeCommand('stripe-connect', { action: 'resolve-review', caseId, status, notes });
export const requestRefund = (
  organisationId: string,
  paymentId: string,
  amountMinor: number,
  reason: string
) =>
  edgeCommand('stripe-payment', {
    action: 'refund',
    organisationId,
    paymentId,
    amountMinor,
    reason,
    requestId: crypto.randomUUID()
  });

export type PaymentConfiguration = {
  stripe_connected_account_id: string | null;
  onboarding_status: string;
  environment: string;
  charges_enabled: boolean;
  payouts_enabled: boolean;
  card_payments_capability: string;
  afterpay_capability_status: string;
  requirements_currently_due: string[];
  requirements_past_due: string[];
  requirements_disabled_reason: string | null;
  last_synchronised_at: string | null;
};
export type PaymentAttempt = {
  id: string;
  status: string;
  payment_option: string;
  requested_amount_minor: number;
  currency: string;
  failure_code: string | null;
  failure_reason: string | null;
  created_at: string;
};
export type Payment = {
  id: string;
  status: string;
  captured_amount_minor: number;
  refunded_amount_minor: number;
  processor_fee_minor: number | null;
  processor_net_minor: number | null;
  fee_reconciliation_status: string;
  currency: string;
  payment_method_family: string;
  paid_at: string;
  customer_name: string | null;
  service_names: string | null;
  booking_total_minor: number;
  outstanding_amount_minor: number;
};
export type Refund = {
  id: string;
  payment_id: string;
  status: string;
  requested_amount_minor: number;
  refunded_amount_minor: number;
  currency: string;
  failure_code: string | null;
  created_at: string;
};
export type ReviewCase = {
  id: string;
  payment_id: string;
  status: string;
  reason_code: string;
  reason_detail: string | null;
  resolution_notes: string | null;
  created_at: string;
};
export type Alert = {
  id: string;
  alert_type: string;
  severity: string;
  status: string;
  created_at: string;
};
